/**
 * form-embed D8 — the form on a website page, behavior half. It reads the form by its website name and holds
 * the answers and the ticket in page memory only: no browser storage, no server draft. Draws nothing.
 *
 * - A signed-in visitor just sends; a guest adds a name and an email, or signs in from the offer, which swaps
 *   the guest fields for who they are and keeps every answer (the questions are never redrawn).
 * - A ticket is held, never replaced, until it is old enough: Send pressed at once waits, then goes once.
 *   Only a refused ticket is read again, twice at most, and the fresh one is held the same way.
 * - A form updated meanwhile is read again: each answer whose question still exists with the same type is
 *   kept, the rest are dropped, and the changed line shows.
 * - With two steps, Next is the questions' own submit, so it checks them as Send would; the questions stay
 *   drawn, hidden, on the details step, so Back finds every answer where it was.
 * - The form's reply setting (D12) picks the words: a form that does not ask where to reply promises none.
 *
 * site-form-buy-mode D1: placed with a product (one option, on sale here), the questions show in one step and the
 * button holds the answers, puts the product in the bag carrying them and opens checkout. It asks no name or email
 * (checkout does), keeps the sign-in offer, counts as asking where to reply, and reads Sold out when none are left.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FormField, PublicFormField } from '@telify/shared';
import { LEGAL_PAGE_PATHS } from '@telify/shared/browser';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';

import { applyIdentity, clientName, sitePost, useTelegramSignIn, useWebSession } from '../../api/session';
import { ownCheckout, useBagPrice, useShopCatalog } from '../../api/shop';
import { SiteFetchError, siteFetch, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, usePlaced, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useBag } from '../../hooks/useBag';
import { usePageMover } from '../../pageMover';
import { readPluginOption } from '../../site';
import { contract, type SiteFormActions, type SiteFormEvents, type SiteFormState } from './contract';

export type SiteFormInput = PieceInput<SiteFormEvents>;

/** The sign-in offer's words, with the studio's name in place. */
export interface SiteFormOffer {
  readonly heading: string;
  readonly lead: string | null;
  readonly benefits: readonly { readonly title: string | null; readonly line: string }[];
}

export type SiteFormRuntime = PieceRuntime<typeof contract, SiteFormState, SiteFormActions> & {
  /** The form drawn, once read. */
  readonly form: FormDefinition | null;
  /** Changes when the form was read again after it changed, so the questions are drawn afresh with the kept answers. */
  readonly formKey: number;
  readonly serverErrors: FieldError[];
  /** False until the website session is read: the details wait for it. */
  readonly sessionKnown: boolean;
  readonly signIn: { readonly ready: boolean; readonly pending: boolean; readonly failed: boolean };
  readonly words: { readonly detailsHeading: string; readonly signedInLine: string; readonly note: string; readonly doneText: string; readonly offer: SiteFormOffer };
  readonly privacyHref: string;
  /** The studio's bot, for the open-the-bot line. */
  readonly botHref: string | null;
  /** The off-screen trap field (D7): a person never fills it. */
  readonly trapRef: RefObject<HTMLInputElement>;
};

interface SiteFormReadDto {
  readonly form: {
    readonly id: string;
    readonly name: string;
    readonly description: string | null;
    readonly asksReply: boolean;
    readonly fields: PublicFormField[];
  };
  readonly ticket: string;
}

/** site-form-buy-mode D2: the answers held for the purchase, and the option they are for. */
interface HoldDto {
  readonly answersId: string;
  readonly answersKey: string;
  readonly optionId: string;
}

interface Loaded {
  readonly form: FormDefinition;
  readonly asksReply: boolean;
  readonly ticket: string;
  /** When the ticket was read here: the hold counts from then, never earlier than the server issued it. */
  readonly readAt: number;
  readonly version: number;
}

interface Pending {
  readonly responses: FormResponseInput[];
  readonly guest: { readonly name: string; readonly email: string } | null;
}

/** The server refuses a ticket younger than 3 seconds; the hold adds a margin. */
const TICKET_HOLD_MS = 3_100;
const TRY_LATER = 'Please try again in a little while.';
const COULD_NOT_SEND = "Couldn't send your answers. Please try again.";
const NO_LINES: readonly { optionId: string; quantity: number }[] = [];
const BAG_FULL = 'Your bag is full. Take something out of it, then try again.';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function definition(read: SiteFormReadDto['form'], savedData?: Record<string, unknown>): FormDefinition {
  return {
    formId: read.id,
    formType: 'GENERAL',
    title: read.name,
    description: read.description ?? undefined,
    // The public projection carries no personal-data marks; the website never shows them.
    fields: read.fields.map((f) => ({ ...f, isPII: false }) as FormField),
    ...(savedData ? { savedData } : {}),
  };
}

/** The answers whose question still exists with the same type. */
function keptAnswers(responses: FormResponseInput[], before: FormDefinition, after: SiteFormReadDto['form']): Record<string, unknown> {
  const typeBefore = new Map(before.fields.map((f) => [f.fieldId, f.type]));
  const typeAfter = new Map(after.fields.map((f) => [f.fieldId, f.type]));
  const kept: Record<string, unknown> = {};
  for (const r of responses) {
    const type = typeAfter.get(r.fieldId);
    if (type !== undefined && type === typeBefore.get(r.fieldId)) kept[r.fieldId] = r.value;
  }
  return kept;
}

function fieldErrorsOf(err: SiteFetchError): FieldError[] {
  const list = (err.body as { fieldErrors?: unknown }).fieldErrors;
  return Array.isArray(list)
    ? list.filter((e): e is FieldError => typeof e === 'object' && e !== null && typeof (e as FieldError).fieldId === 'string' && typeof (e as FieldError).error === 'string')
    : [];
}

export function useSiteForm(input: SiteFormInput): SiteFormRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const placed = usePlaced();
  const queryClient = useQueryClient();
  const studio = useSiteStudio().data;
  const session = useWebSession();
  const telegram = useTelegramSignIn();
  const key = options.form;

  // site-form-buy-mode D1: the product the form leads into buying, read from the shop's catalog.
  const buying = options.product != null;
  const catalog = useShopCatalog(buying);
  const bag = useBag();
  const refetchCatalog = catalog.refetch;
  const move = usePageMover();
  const forSale = useMemo(() => {
    const product = catalog.data?.products.find((p) => p.handle === options.product);
    return product && product.options.length === 1 ? product.options[0]! : null;
  }, [catalog.data, options.product]);
  // The catalog counts no one's own: when it says none are left and this tab has its own unpaid order, the bag's
  // price, which counts what that order sets aside, says whether the last one is still this buyer's (walk row 7).
  const checkingOwn = buying && forSale?.soldOut === true && Boolean(ownCheckout().own);
  const ownCheck = useBagPrice(checkingOwn && forSale ? [{ optionId: forSale.id, quantity: 1 }] : NO_LINES);
  const ownsLast = checkingOwn && ownCheck.data?.lines.some((l) => l.optionId === forSale?.id && !l.soldOut) === true;
  // A hold refused as sold out reads Sold out until the catalog is read again.
  const [soldOutAt, setSoldOutAt] = useState<number | null>(null);
  // A product the catalog no longer lists (hidden, or no longer sold) reads Sold out too, never an empty space
  // (shop-self-fulfilment walk, Gabriel 2026-11-01).
  const notOnSale = buying && Boolean(catalog.data) && !forSale;
  const soldOut = buying && (notOnSale || (forSale?.soldOut === true && !ownsLast) || (soldOutAt !== null && soldOutAt === catalog.dataUpdatedAt));

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [status, setStatus] = useState<SiteFormState['status']>('loading');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestNameError, setGuestNameError] = useState<string | null>(null);
  const [guestEmailError, setGuestEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  const [sent, setSent] = useState<{ asGuest: string | null; botLine: boolean } | null>(null);
  const [step, setStep] = useState<SiteFormState['step']>('questions');

  const loadedRef = useRef<Loaded | null>(null);
  loadedRef.current = loaded;
  const pending = useRef<Pending | null>(null);
  const refreshes = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const trapRef = useRef<HTMLInputElement>(null);
  const canMessage = useRef<boolean | null>(null);
  canMessage.current = telegram.canMessage;

  const read = useCallback(() => siteFetch<SiteFormReadDto>(`/forms/${encodeURIComponent(key ?? '')}`), [key]);

  const load = useCallback(async () => {
    if (!key) {
      setStatus('unavailable');
      return;
    }
    setStatus('loading');
    try {
      const r = await read();
      setLoaded({ form: definition(r.form), asksReply: r.form.asksReply, ticket: r.ticket, readAt: Date.now(), version: 0 });
      setStatus('form');
    } catch (err) {
      setStatus(err instanceof SiteFetchError && err.status === 404 ? 'unavailable' : 'error');
    }
  }, [key, read]);

  useEffect(() => {
    void load();
    return () => clearTimeout(timer.current);
  }, [load]);

  // site-form-buy-mode (code review Medium 2): Back from checkout can bring the page back as it was left, still busy
  // from the claim that opened checkout; it is the form again, every answer kept, ready to claim again.
  useEffect(() => {
    if (!buying) return;
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted && !pending.current) setStatus((now) => (now === 'sending' ? 'form' : now));
    };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, [buying]);

  // D8 / 7.4: a placeholder whose form cannot be read here stays empty, and says why once.
  const warned = useRef(false);
  useEffect(() => {
    if (placed && status === 'unavailable' && !warned.current) {
      warned.current = true;
      console.warn(`[telify] site-form: the form "${key ?? ''}" is not on this website; its placeholder stays empty.`);
    }
  }, [placed, status, key]);
  const productMissing = buying && catalog.isFetched && !forSale;
  useEffect(() => {
    if (placed && productMissing && !warned.current) {
      warned.current = true;
      console.warn(`[telify] site-form: the product "${options.product ?? ''}" is not on sale here with one option; the form reads Sold out.`);
    }
  }, [placed, productMissing, options.product]);

  /** Back to the form after a refusal, the answers kept. */
  const backToForm = useCallback((line: string | null) => {
    pending.current = null;
    setError(line);
    setStatus('form');
  }, []);

  const attemptRef = useRef<() => Promise<void>>(async () => {});

  /** Sends once the ticket is old enough. */
  const schedule = useCallback(() => {
    const l = loadedRef.current;
    if (!l) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void attemptRef.current(), Math.max(0, l.readAt + TICKET_HOLD_MS - Date.now()));
  }, []);

  attemptRef.current = async () => {
    const l = loadedRef.current;
    const p = pending.current;
    if (!l || !p || !key) return;
    const trap = trapRef.current?.value;
    try {
      if (buying) {
        const held = await sitePost<HoldDto>(`/forms/${encodeURIComponent(key)}/hold`, {
          responses: p.responses,
          ticket: l.ticket,
          product: options.product,
          ...(options.paidLine ? { paidLine: options.paidLine } : {}),
          ...ownCheckout(),
          ...(trap ? { website: trap } : {}),
        });
        pending.current = null;
        refreshes.current = 0;
        if (!bag.claim(held.optionId, { id: held.answersId, key: held.answersKey }, forSale?.price)) return backToForm(BAG_FULL);
        // Still busy while checkout opens.
        return move('/shop/checkout');
      }
      const result = await sitePost<{ success: true; canReplyInTelegram?: boolean }>(`/forms/${encodeURIComponent(key)}/send`, {
        responses: p.responses,
        ticket: l.ticket,
        ...(p.guest ? { guest: p.guest } : {}),
        ...(trap ? { website: trap } : {}),
      });
      pending.current = null;
      refreshes.current = 0;
      // A form that does not ask where to reply promised nothing, so it never asks them to open the bot.
      setSent({
        asGuest: p.guest?.email ?? null,
        botLine: l.asksReply && !p.guest && (result.canReplyInTelegram === false || canMessage.current === false),
      });
      setStatus('sent');
      emit('sent', { formId: l.form.formId });
    } catch (err) {
      if (!(err instanceof SiteFetchError)) return backToForm(COULD_NOT_SEND);
      switch (err.code) {
        case 'TICKET_INVALID':
        case 'FORM_CHANGED': {
          if (err.code === 'TICKET_INVALID' && refreshes.current >= 2) return backToForm(COULD_NOT_SEND);
          refreshes.current += 1;
          try {
            const r = await read();
            const sameQuestions = JSON.stringify(r.form.fields) === JSON.stringify(l.form.fields.map(({ isPII: _pii, ...f }) => f));
            if (err.code === 'TICKET_INVALID' && sameQuestions) {
              // The same form: only the ticket is new, and it is held like the first.
              setLoaded({ ...l, ticket: r.ticket, readAt: Date.now() });
              loadedRef.current = { ...l, ticket: r.ticket, readAt: Date.now() };
              return schedule();
            }
            setLoaded({
              form: definition(r.form, keptAnswers(p.responses, l.form, r.form)),
              asksReply: r.form.asksReply,
              ticket: r.ticket,
              readAt: Date.now(),
              version: l.version + 1,
            });
            setChanged(true);
            setStep('questions');
            return backToForm(null);
          } catch (readErr) {
            if (readErr instanceof SiteFetchError && readErr.status === 404) {
              pending.current = null;
              return setStatus('unavailable');
            }
            return backToForm(COULD_NOT_SEND);
          }
        }
        case 'SIGNED_OUT':
        case 'GUEST_DETAILS_REQUIRED':
          // The session ended elsewhere: the guest fields come back, the answers stay.
          applyIdentity(queryClient, { signedIn: false });
          return backToForm(null);
        case 'VALIDATION_FAILED': {
          const errors = fieldErrorsOf(err);
          setGuestNameError(errors.find((e) => e.fieldId === 'guest.name')?.error ?? null);
          setGuestEmailError(errors.find((e) => e.fieldId === 'guest.email')?.error ?? null);
          const questionErrors = errors.filter((e) => !e.fieldId.startsWith('guest.'));
          setServerErrors(questionErrors);
          // A refused answer shows where it was given.
          if (questionErrors.length > 0) setStep('questions');
          return backToForm(null);
        }
        case 'TRY_LATER':
          return backToForm(TRY_LATER);
        case 'SOLD_OUT':
          // Sold out meanwhile: the button reads so, and the catalog read again decides.
          setSoldOutAt(catalog.dataUpdatedAt);
          void catalog.refetch();
          return backToForm(null);
        case 'ITEM_UNAVAILABLE':
          void catalog.refetch();
          return backToForm(err.message);
        case 'NOT_FOUND':
          pending.current = null;
          return setStatus('unavailable');
        default:
          return backToForm(COULD_NOT_SEND);
      }
    }
  };

  const signedIn = session.status === 'signedIn';
  // site-form-buy-mode D1: buying shows the questions in one step.
  const twoSteps = options.steps === 'two' && !buying;
  const actions = useMemo<SiteFormActions>(
    () => ({
      // The questions' submit only calls it once they pass, and they keep their answers while hidden.
      next: () => {
        if (!twoSteps) return;
        setError(null);
        setStep('details');
      },
      back: () => setStep('questions'),
      signIn: () => telegram.signIn(),
      signInAsTestAccount: telegram.signInAsTestAccount,
      setGuestName: (name) => {
        setGuestName(name);
        setGuestNameError(null);
      },
      setGuestEmail: (email) => {
        setGuestEmail(email);
        setGuestEmailError(null);
      },
      send: (responses) => {
        if (pending.current || !loadedRef.current || soldOut) return;
        let guest: Pending['guest'] = null;
        // Buying asks no name or email: checkout does.
        if (!signedIn && !buying) {
          const name = guestName.trim();
          const email = guestEmail.trim();
          const nameError = name ? null : 'Enter your name.';
          const emailError = !email ? 'Enter your email.' : EMAIL.test(email) ? null : 'Enter an email like name@example.com.';
          setGuestNameError(nameError);
          setGuestEmailError(emailError);
          if (nameError || emailError) return;
          guest = { name, email };
        }
        pending.current = { responses: responses as FormResponseInput[], guest };
        setError(null);
        setChanged(false);
        setServerErrors([]);
        setStatus('sending');
        schedule();
      },
      retry: () => {
        void load();
        if (buying) void refetchCatalog();
      },
    }),
    [twoSteps, telegram, signedIn, buying, soldOut, guestName, guestEmail, schedule, load, refetchCatalog],
  );

  const studioName = studio?.name ?? 'the studio';
  // site-form-buy-mode: buying counts as a form that asks where to reply.
  const asksReply = buying || (loaded?.asksReply ?? false);
  // This placement's own offer lines, when it sets them and they fit (one to six, each at most 200 characters).
  const ownLines = useMemo(() => {
    if (options.offerLines == null) return null;
    const lines = options.offerLines.split('|').map((l) => l.trim()).filter(Boolean);
    if (lines.length >= 1 && lines.length <= 6 && lines.every((l) => l.length <= 200)) return lines;
    console.warn(`[telify] ${contract.name}: option "offerLines" must be one to six lines of at most 200 characters; using the site's lines.`);
    return null;
  }, [options.offerLines]);
  const words = useMemo(() => {
    const set = readPluginOption('forms', 'signInOffer') as {
      heading: string;
      lead?: string;
      benefits: { title?: string; line: string; when?: 'always' | 'askingWhereToReply' }[];
    };
    const named = (text: string) => text.split('{studio}').join(studioName);
    // Each kind of form reads only its own words, so a site's words for one never show on the other (D8).
    const doneText = !asksReply
      ? named(options.doneThanks ?? 'Thanks. {studio} has your answers.')
      : sent?.asGuest
        ? (options.doneGuest ?? "You'll hear back at {email}.").split('{email}').join(sent.asGuest)
        : (options.doneSignedIn ?? "You'll hear back in Telegram.");
    return {
      detailsHeading: !asksReply
        ? (options.yourDetailsHeading ?? 'Your details')
        : signedIn
          ? named(options.signedInHeading ?? '{studio} will reply to you in Telegram')
          : (options.detailsHeading ?? 'Where should we reply?'),
      signedInLine: named(options.signedInLine ?? 'Signed in with Telegram as {name}.'),
      note: options.note ?? `Your details go only to ${studioName}.`,
      doneText,
      offer: {
        heading: named(set.heading),
        lead: set.lead ? named(set.lead) : null,
        benefits: ownLines
          ? ownLines.map((line) => ({ title: null, line: named(line) }))
          : set.benefits
              .filter((b) => asksReply || b.when !== 'askingWhereToReply')
              .map((b) => ({ title: b.title ? named(b.title) : null, line: named(b.line) })),
      },
    };
  }, [asksReply, signedIn, ownLines, options.detailsHeading, options.yourDetailsHeading, options.signedInHeading, options.signedInLine, options.note, options.doneGuest, options.doneSignedIn, options.doneThanks, sent, studioName]);

  const bot = studio?.bookingsBotUsername ?? null;
  // Buying waits on the catalog too; a product not on sale here reads Sold out (notOnSale).
  const shown: SiteFormState['status'] =
    !buying || status !== 'form'
      ? status
      : catalog.isPending
        ? 'loading'
        : catalog.isError && !catalog.data
          ? catalog.error instanceof SiteFetchError && catalog.error.status === 404
            ? 'unavailable'
            : 'error'
          : checkingOwn && !ownCheck.data && !ownCheck.isError
            ? 'loading'
            : status;
  const state: SiteFormState = {
    status: shown,
    step: twoSteps ? step : 'questions',
    asksReply,
    signedInAs: signedIn && session.user ? clientName(session.user).name : null,
    guestName,
    guestEmail,
    guestNameError,
    guestEmailError,
    error,
    changed,
    openBot: sent?.botLine && bot ? `@${bot}` : null,
    testAccount: Boolean(telegram.signInAsTestAccount),
    buying,
    soldOut,
  };

  return {
    state,
    actions,
    options,
    form: loaded?.form ?? null,
    formKey: loaded?.version ?? 0,
    serverErrors,
    sessionKnown: session.status !== 'loading',
    signIn: { ready: telegram.ready, pending: telegram.state === 'pending', failed: telegram.state === 'error' || telegram.state === 'unavailable' },
    words,
    privacyHref: LEGAL_PAGE_PATHS.privacyPolicy,
    botHref: bot ? `https://t.me/${bot}` : null,
    trapRef,
  };
}
